import React, { useState, useEffect, useRef, useMemo } from "react";
import PropTypes from "prop-types";
import cx from "classnames";
import { makeStyles } from "@material-ui/core/styles";
import {
  Box,
  Tooltip,
  IconButton,
  Dialog,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Checkbox,
  Chip,
  ClickAwayListener,
} from "@material-ui/core";
import { ThreeDots } from "react-loader-spinner";
import {
  Description,
  CheckCircle,
  Info,
  LockOpen,
  Save,
  Stars,
  Remove,
  Add,
  GetApp,
  PictureAsPdf,
  Slideshow,
  ExpandMore,
  ExpandLess,
  Fullscreen,
  Visibility,
  VisibilityOff,
} from "@material-ui/icons";
import Button from "components/CustomButtons/Button";
import ModalHeader from "components/ModalHeader";
import GridContainer from "components/Grid/GridContainer";
import GridItem from "components/Grid/GridItem";
import Card from "components/Card/Card";
import CardHeader from "components/Card/CardHeader";
import CardIcon from "components/Card/CardIcon";
import CardBody from "components/Card/CardBody";
import FeedbackButton from "components/CTSGuide/FeedbackButton";
import TextEditor from "components/TextEditor/TextEditor";
import AudioRecorder from "components/AudioRecorder";
import AiLoadingIndicator from "components/AiLoadingIndicator";
import { useTranslation } from "react-i18next";
import { client } from "services/axiosClient";
import ctsGuidePortalStyle from "assets/jss/material-dashboard-pro-react/views/ctsGuidePortalStyle";
import useThemeColors from "hooks/useThemeColors";
import CTSMiniNav from "components/CTSGuide/CTSMiniNav";
import {
  grayColor,
  whiteColor,
  blendColors,
} from "assets/jss/material-dashboard-pro-react";

const useStyles = makeStyles((theme) => ({
  ...ctsGuidePortalStyle(theme),
  // Container layout
  mainContainer: {
    display: "flex",
    gap: "24px",
    marginTop: "16px",
  },
  // Sidebar P&L Tree
  sidebarContainer: {
    width: "320px",
    flexShrink: 0,
  },
  plHeader: {
    color: theme.palette.custom?.clientTextColor || whiteColor,
    borderRadius: "5px 5px 0 0",
    paddingLeft: "10px",
    paddingRight: "10px",
    textAlign: "left",
    fontSize: "12px",
    fontWeight: "500",
    paddingTop: "6px",
    paddingBottom: "6px",
    marginBottom: "0",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    backgroundColor: theme.palette.custom?.clientColor || grayColor[2],
  },
  headerTitle: {
    fontSize: "13px",
    fontWeight: "500",
  },
  plOuterWrapper: {
    width: "100%",
    padding: "0",
    display: "block",
    fontSize: "12px",
    color: grayColor[6],
    overflow: "hidden",
    border: `1px solid ${grayColor[8]}`,
    borderTop: "none",
    borderRadius: "0 0 5px 5px",
    backgroundColor: whiteColor,
  },
  plLineWrapper: {
    width: "100%",
    minHeight: "36px",
    display: "flex",
    alignItems: "center",
    lineHeight: "1.5",
    padding: "0 8px",
    transition: "all 0.15s ease",
    cursor: "pointer",
    borderBottom: `1px solid ${grayColor[9]}`,
    "&:last-child": {
      borderBottom: "none",
    },
    "&:hover": {
      backgroundColor: grayColor[10],
    },
  },
  plLineSelected: {
    backgroundColor: (props) =>
      blendColors(grayColor[8], props.clientColor || grayColor[2], 0.85) +
      " !important",
  },
  plTotal: {
    backgroundColor: grayColor[8],
    fontWeight: "bold",
    textTransform: "uppercase",
    fontSize: "11px",
  },
  plSubTotal: {
    backgroundColor: grayColor[11],
    fontWeight: "500",
  },
  plLevel3: {
    color: grayColor[1],
  },
  plLineDescription: {
    flex: 1,
    paddingLeft: "8px",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  drillDownButton: {
    padding: "2px",
    marginRight: "4px",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    "& svg": {
      fontSize: "14px",
    },
    "&:hover": {
      backgroundColor: "rgba(0,0,0,0.08)",
      borderRadius: "3px",
    },
  },
  lineName: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    flex: 1,
  },
  lineStatus: {
    marginLeft: "auto",
    display: "flex",
    alignItems: "center",
    "& svg": {
      fontSize: "16px",
    },
  },
  // Content area
  contentContainer: {
    flex: 1,
    minWidth: 0,
  },
  contentHeader: {
    marginBottom: "20px",
    paddingBottom: "16px",
    borderBottom: `1px solid ${grayColor[9]}`,
  },
  lineTitle: {
    fontSize: "18px",
    fontWeight: "600",
    margin: 0,
    color: grayColor[1],
  },
  // Form
  formField: {
    marginBottom: "20px",
  },
  formLabel: {
    display: "flex",
    alignItems: "center",
    gap: "4px",
    marginBottom: "8px",
    fontSize: "14px",
    fontWeight: "500",
    color: grayColor[2],
  },
  // Approved banner (green)
  lockedBanner: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "12px 16px",
    backgroundColor: "#e8f5e9",
    border: "1px solid #81c784",
    borderRadius: "4px",
    marginBottom: "20px",
  },
  lockedBannerText: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    color: "#2e7d32",
    fontSize: "14px",
    "& svg": {
      fontSize: "20px",
    },
  },
  // AI Results
  aiResultContainer: {
    marginTop: "24px",
  },
  aiResultTitle: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    fontSize: "14px",
    fontWeight: "600",
    marginBottom: "12px",
  },
  feedbackList: {
    margin: 0,
    paddingLeft: "20px",
    listStyle: "disc",
  },
  feedbackItem: {
    marginBottom: "8px",
    fontSize: "13px",
    color: grayColor[2],
  },
  // Action buttons row
  actionsRow: {
    display: "flex",
    gap: "8px",
    marginTop: "20px",
    paddingTop: "16px",
    borderTop: `1px solid ${grayColor[9]}`,
  },
  // Empty state
  emptyState: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "60px 20px",
    color: grayColor[3],
    textAlign: "center",
  },
  // Edit dialog
  dialogContent: {
    paddingTop: "10px",
  },
  // Unsaved changes indicator
  unsavedIndicator: {
    fontSize: "0.75em",
    color: "#ff9800",
    fontWeight: 500,
    marginLeft: "12px",
  },
  // Input selector styles
  inputSelectorContainer: {
    marginBottom: "16px",
    padding: "12px 16px",
    backgroundColor: "#f8f9fa",
    borderRadius: "6px",
    border: `1px solid ${grayColor[9]}`,
  },
  inputSelectorLabel: {
    fontSize: "13px",
    fontWeight: "500",
    color: grayColor[2],
    marginBottom: "8px",
    display: "flex",
    alignItems: "center",
    gap: "6px",
  },
  inputChipsContainer: {
    display: "flex",
    flexWrap: "wrap",
    gap: "6px",
    alignItems: "center",
  },
  inputChip: {
    height: "26px",
    fontSize: "12px",
    backgroundColor: "#e3f2fd",
    "& .MuiChip-deleteIcon": {
      fontSize: "16px",
    },
  },
  addInputButton: {
    height: "26px",
    minWidth: "26px",
    padding: "0 8px",
    fontSize: "12px",
    borderRadius: "13px",
    border: `1px dashed ${grayColor[5]}`,
    color: grayColor[3],
    backgroundColor: "transparent",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: "4px",
    transition: "all 0.2s ease",
    "&:hover": {
      backgroundColor: grayColor[10],
      borderColor: grayColor[3],
    },
  },
  inputDropdown: {
    position: "absolute",
    top: "100%",
    left: 0,
    zIndex: 1000,
    backgroundColor: whiteColor,
    border: `1px solid ${grayColor[8]}`,
    borderRadius: "4px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
    maxHeight: "250px",
    overflowY: "auto",
    minWidth: "280px",
    marginTop: "4px",
  },
  inputDropdownItem: {
    padding: "10px 12px",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: "10px",
    fontSize: "12px",
    borderBottom: `1px solid ${grayColor[10]}`,
    "&:last-child": {
      borderBottom: "none",
    },
    "&:hover": {
      backgroundColor: grayColor[10],
    },
  },
  inputDropdownItemSelected: {
    backgroundColor: "#e3f2fd",
  },
  inputDropdownEmpty: {
    padding: "12px",
    color: grayColor[4],
    fontSize: "12px",
    textAlign: "center",
  },
  // Expanded editor dialog styles
  expandedDialog: {
    "& .MuiDialog-paper": {
      width: "90vw",
      maxWidth: "1200px",
      height: "85vh",
      maxHeight: "85vh",
    },
  },
  expandedDialogContent: {
    padding: "20px",
    display: "flex",
    flexDirection: "column",
    flex: 1,
    overflow: "hidden",
    "& .rdw-editor-wrapper": {
      flex: 1,
      display: "flex",
      flexDirection: "column",
    },
    "& .rdw-editor-main": {
      flex: 1,
      overflow: "auto",
      minHeight: "unset !important",
    },
  },
  expandButton: {
    padding: "4px",
    marginLeft: "4px",
  },
  // Example toggle button styles
  exampleToggle: {
    display: "flex",
    alignItems: "center",
    gap: "4px",
    color: grayColor[3],
    cursor: "pointer",
    padding: "2px 6px",
    borderRadius: "4px",
    marginLeft: "auto",
    fontSize: "12px",
    transition: "all 0.2s ease",
    "&:hover": {
      color: "#790000",
      backgroundColor: "rgba(121, 0, 0, 0.08)",
    },
  },
  exampleToggleText: {
    fontSize: "11px",
    fontWeight: 500,
  },
  exampleContainer: {
    marginBottom: "16px",
    padding: "12px",
    backgroundColor: grayColor[10],
    borderRadius: "8px",
    border: `1px solid ${grayColor[8]}`,
  },
  exampleLabel: {
    fontSize: "11px",
    fontWeight: 600,
    color: grayColor[3],
    marginBottom: "8px",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  },
  exampleInputsNote: {
    fontSize: "12px",
    color: grayColor[1],
    fontWeight: 500,
    marginBottom: "10px",
    padding: "8px 12px",
    backgroundColor: "#fff",
    borderRadius: "4px",
    border: `1px solid ${grayColor[8]}`,
    "& strong": {
      color: "#790000",
      fontWeight: 600,
    },
  },
}));

// Helper function to recursively get all leaf children status
function getChildrenStatus(line) {
  if (!line.children || line.children.length === 0) {
    // This is a leaf node, return its own status
    if (!line.documentation) return "not_started";
    return line.documentation.status || "not_started";
  }

  // Recursively get all children statuses
  const childStatuses = line.children.map((child) => getChildrenStatus(child));

  // If all children are submitted, parent is submitted
  const allSubmitted = childStatuses.every((s) => s === "submitted");
  if (allSubmitted) return "submitted";

  // If any child has documentation (submitted or in_progress), parent is in_progress
  const anyStarted = childStatuses.some(
    (s) => s === "submitted" || s === "in_progress",
  );
  if (anyStarted) return "in_progress";

  return "not_started";
}

// PLLine component for sidebar
function PLLine({
  line,
  selectedLine,
  onSelect,
  drillDownsOpen,
  onDrillDownClick,
  classes,
}) {
  const { t } = useTranslation();
  const hasChildren = line.children && line.children.length > 0;
  const isOpen = drillDownsOpen.includes(line.id);
  // Parent lines (with children) cannot be selected for documentation
  const isSelected = !hasChildren && selectedLine?.id === line.id;
  // Use line.level from backend (1, 2, or 3) for visual styling
  const visualLevel = line.level || 1;

  // Handle click - for parent lines, toggle expand; for leaf lines, select
  const handleClick = () => {
    if (hasChildren) {
      // Parent line: just toggle expand/collapse
      onDrillDownClick(line.id);
    } else {
      // Leaf line: select for documentation
      onSelect(line);
    }
  };

  const getStatusInfo = () => {
    // For parent lines, derive status from children
    if (hasChildren) {
      const aggregatedStatus = getChildrenStatus(line);
      const childCount = line.children.length;

      if (aggregatedStatus === "submitted") {
        return {
          icon: <CheckCircle style={{ color: "#4caf50" }} />,
          tooltip: t("ctsguide.documentation.status_parent_all_complete", {
            count: childCount,
          }),
        };
      }
      if (aggregatedStatus === "in_progress") {
        return {
          icon: <Description style={{ color: "#ff9800" }} />,
          tooltip: t("ctsguide.documentation.status_parent_partial", {
            count: childCount,
          }),
        };
      }
      return {
        icon: <Description style={{ color: "#999" }} />,
        tooltip: t("ctsguide.documentation.status_parent_not_started", {
          count: childCount,
        }),
      };
    }

    // For leaf lines, use their own status
    if (!line.documentation) {
      return {
        icon: <Description style={{ color: "#999" }} />,
        tooltip: t("ctsguide.documentation.status_not_started"),
      };
    }
    if (line.documentation.status === "submitted") {
      return {
        icon: <CheckCircle style={{ color: "#4caf50" }} />,
        tooltip: t("ctsguide.documentation.status_submitted"),
      };
    }
    if (line.documentation.status === "in_progress") {
      return {
        icon: <Description style={{ color: "#ff9800" }} />,
        tooltip: t("ctsguide.documentation.status_in_progress"),
      };
    }
    return {
      icon: <Description style={{ color: "#999" }} />,
      tooltip: t("ctsguide.documentation.status_not_started"),
    };
  };

  const statusInfo = getStatusInfo();

  return (
    <>
      <div
        className={cx({
          [classes.plLineWrapper]: true,
          [classes.plTotal]: visualLevel === 1,
          [classes.plSubTotal]: visualLevel === 2,
          [classes.plLevel3]: visualLevel >= 3,
          [classes.plLineSelected]: isSelected,
        })}
        onClick={handleClick}
        style={{
          paddingLeft: `${(visualLevel - 1) * 16 + 8}px`,
          cursor: hasChildren ? "pointer" : "pointer",
        }}
      >
        <div className={classes.plLineDescription}>
          {hasChildren && (
            <span className={classes.drillDownButton}>
              {isOpen ? <Remove /> : <Add />}
            </span>
          )}
          <span className={classes.lineName}>{line.name}</span>
        </div>
        <Tooltip title={statusInfo.tooltip} arrow placement="right">
          <div className={classes.lineStatus}>{statusInfo.icon}</div>
        </Tooltip>
      </div>

      {/* Render children if expanded */}
      {hasChildren &&
        isOpen &&
        line.children.map((child) => (
          <PLLine
            key={child.id}
            line={child}
            selectedLine={selectedLine}
            onSelect={onSelect}
            drillDownsOpen={drillDownsOpen}
            onDrillDownClick={onDrillDownClick}
            classes={classes}
          />
        ))}
    </>
  );
}

PLLine.propTypes = {
  line: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    level: PropTypes.number,
    children: PropTypes.array,
    documentation: PropTypes.shape({
      status: PropTypes.string,
    }),
  }).isRequired,
  selectedLine: PropTypes.shape({
    id: PropTypes.string,
  }),
  onSelect: PropTypes.func.isRequired,
  drillDownsOpen: PropTypes.arrayOf(PropTypes.string).isRequired,
  onDrillDownClick: PropTypes.func.isRequired,
  classes: PropTypes.object.isRequired,
};

export default function Documentation() {
  const { clientColor, clientTextColor } = useThemeColors();
  const classes = useStyles({ clientColor });
  const { t } = useTranslation();

  const [plLines, setPlLines] = useState([]);
  const [selectedLine, setSelectedLine] = useState(null);
  const [documentation, setDocumentation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [validating, setValidating] = useState(false);
  const [approving, setApproving] = useState(false);
  const [drillDownsOpen, setDrillDownsOpen] = useState([]);

  // Available inputs for this project
  const [availableInputs, setAvailableInputs] = useState([]);
  // Selected inputs for current documentation (IDs)
  const [selectedInputIds, setSelectedInputIds] = useState([]);
  // Input selector dropdown open state
  const [inputSelectorOpen, setInputSelectorOpen] = useState(false);

  // Expanded editor dialog state (null or field name: 'business_context', 'calculation_logic', 'observations')
  const [expandedEditor, setExpandedEditor] = useState(null);

  // Show example states for each editor
  const [showBusinessContextExample, setShowBusinessContextExample] =
    useState(false);
  const [showCalculationLogicExample, setShowCalculationLogicExample] =
    useState(false);
  const [showObservationsExample, setShowObservationsExample] = useState(false);

  // Memoized list of selected inputs for mention suggestions in TextEditor
  const selectedInputsForMention = useMemo(() => {
    return availableInputs.filter((input) =>
      selectedInputIds.includes(input.id),
    );
  }, [availableInputs, selectedInputIds]);

  // Refs for TextEditor content (to avoid re-renders on each keystroke)
  const businessContextRef = useRef("");
  const calculationLogicRef = useRef("");
  const observationsRef = useRef("");

  // Initial content for TextEditors (used to initialize editors on line change)
  const [initialBusinessContext, setInitialBusinessContext] = useState("");
  const [initialCalculationLogic, setInitialCalculationLogic] = useState("");
  const [initialObservations, setInitialObservations] = useState("");

  // Track unsaved changes
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const initialValuesRef = useRef({
    business_context: "",
    calculation_logic: "",
    observations: "",
  });

  // AI validated content - editable by user
  const [aiContent, setAiContent] = useState("");
  const [initialAiContent, setInitialAiContent] = useState("");
  const aiContentRef = useRef("");

  // Key to force TextEditor remount when documentation loads (prevents stale content)
  const [editorKey, setEditorKey] = useState(0);

  // Export dialog state
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [exportFormat, setExportFormat] = useState("pdf");
  const [exportIncludeInputs, setExportIncludeInputs] = useState(true);
  const [exportIncludeCalculations, setExportIncludeCalculations] =
    useState(true);
  const [exportOnlyApproved, setExportOnlyApproved] = useState(false);
  const [exporting, setExporting] = useState(false);

  // Collapsible section state
  const [rawInputsExpanded, setRawInputsExpanded] = useState(true);

  useEffect(() => {
    loadPLLines();
  }, []);

  useEffect(() => {
    if (selectedLine) {
      loadDocumentation(selectedLine.id);
    }
  }, [selectedLine]);

  // Initialize all parent lines as open
  useEffect(() => {
    const parentIds = [];
    const findParents = (lineList) => {
      lineList?.forEach((line) => {
        if (line.children && line.children.length > 0) {
          parentIds.push(line.id);
          findParents(line.children);
        }
      });
    };
    findParents(plLines);
    setDrillDownsOpen(parentIds);
  }, [plLines]);

  const loadPLLines = async (preserveSelection = false) => {
    if (!preserveSelection) {
      setLoading(true);
    }
    try {
      // Optimized endpoint with documentation status and available inputs
      const res = await client.get("/api/ctsguide/documentation-page-data/");
      const lines = res.data.pl_lines || [];
      setPlLines(lines);

      // Set available inputs
      setAvailableInputs(res.data.available_inputs || []);

      // Only select first leaf line on initial load (not on refresh)
      if (!preserveSelection && lines.length > 0) {
        const firstLine = findFirstLeaf(lines);
        setSelectedLine(firstLine || lines[0]);
      }
    } catch (error) {
      // Error loading P&L lines
    } finally {
      if (!preserveSelection) {
        setLoading(false);
      }
    }
  };

  const findFirstLeaf = (lines) => {
    for (const line of lines) {
      if (!line.children || line.children.length === 0) {
        return line;
      }
      const leaf = findFirstLeaf(line.children);
      if (leaf) return leaf;
    }
    return null;
  };

  const loadDocumentation = async (lineId) => {
    try {
      const res = await client.get(`/api/ctsguide/documentations/${lineId}/`);
      const doc = res.data;
      setDocumentation(doc);

      // Set initial content for TextEditors
      const businessContext = doc.business_context || "";
      const calculationLogic = doc.calculation_logic || "";
      const observations = doc.observations || "";
      const aiRewritten = doc.ai_rewritten_documentation || "";

      setInitialBusinessContext(businessContext);
      setInitialCalculationLogic(calculationLogic);
      setInitialObservations(observations);
      setAiContent(aiRewritten);
      setInitialAiContent(aiRewritten);

      // Set selected inputs from documentation
      const relatedInputs = doc.related_inputs_data || [];
      setSelectedInputIds(relatedInputs.map((inp) => inp.id));

      // Update refs
      businessContextRef.current = businessContext;
      calculationLogicRef.current = calculationLogic;
      observationsRef.current = observations;
      aiContentRef.current = aiRewritten;

      // Store initial values for comparison (use empty string for template to detect changes)
      initialValuesRef.current = {
        business_context: businessContext,
        calculation_logic: doc.calculation_logic || "",
        observations: observations,
      };

      setHasUnsavedChanges(false);

      // Increment editor key to force TextEditor remount with fresh content
      setEditorKey((prev) => prev + 1);
    } catch (error) {
      console.error("Error loading documentation:", error);
    }
  };

  const checkUnsavedChanges = () => {
    const initial = initialValuesRef.current;
    const hasChanges =
      businessContextRef.current !== initial.business_context ||
      calculationLogicRef.current !== initial.calculation_logic ||
      observationsRef.current !== initial.observations;
    setHasUnsavedChanges(hasChanges);
    return hasChanges;
  };

  // Handlers for TextEditor content changes
  const handleBusinessContextChange = (content) => {
    businessContextRef.current = content;
    checkUnsavedChanges();
  };

  const handleCalculationLogicChange = (content) => {
    calculationLogicRef.current = content;
    checkUnsavedChanges();
  };

  const handleObservationsChange = (content) => {
    observationsRef.current = content;
    checkUnsavedChanges();
  };

  const handleAiContentChange = (content) => {
    aiContentRef.current = content;
  };

  // Speech-to-text endpoint (relative path - fileUploadClient has baseURL)
  const speechToTextEndpoint = "api/ctsguide/documentations/speech-to-text/";

  // Handlers for speech-to-text transcription
  const handleBusinessContextTranscription = (text) => {
    // Append transcribed text to existing content
    const currentContent = businessContextRef.current || "";
    const newContent = currentContent
      ? `${currentContent}<p>${text}</p>`
      : `<p>${text}</p>`;
    businessContextRef.current = newContent;
    setInitialBusinessContext(newContent);
    checkUnsavedChanges();
  };

  const handleCalculationLogicTranscription = (text) => {
    const currentContent = calculationLogicRef.current || "";
    const newContent = currentContent
      ? `${currentContent}<p>${text}</p>`
      : `<p>${text}</p>`;
    calculationLogicRef.current = newContent;
    setInitialCalculationLogic(newContent);
    checkUnsavedChanges();
  };

  const handleObservationsTranscription = (text) => {
    const currentContent = observationsRef.current || "";
    const newContent = currentContent
      ? `${currentContent}<p>${text}</p>`
      : `<p>${text}</p>`;
    observationsRef.current = newContent;
    setInitialObservations(newContent);
    checkUnsavedChanges();
  };

  const handleDrillDownClick = (lineId) => {
    if (drillDownsOpen.includes(lineId)) {
      setDrillDownsOpen(drillDownsOpen.filter((id) => id !== lineId));
    } else {
      setDrillDownsOpen([...drillDownsOpen, lineId]);
    }
  };

  // Handle input selection toggle
  const handleToggleInput = async (inputId) => {
    if (!selectedLine) return;

    const newSelection = selectedInputIds.includes(inputId)
      ? selectedInputIds.filter((id) => id !== inputId)
      : [...selectedInputIds, inputId];

    setSelectedInputIds(newSelection);

    // Save to backend immediately
    try {
      await client.put(`/api/ctsguide/documentations/${selectedLine.id}/`, {
        related_inputs: newSelection,
      });
    } catch (error) {
      console.error("Error updating related inputs:", error);
      // Revert on error
      setSelectedInputIds(selectedInputIds);
    }
  };

  // Remove input from selection
  const handleRemoveInput = async (inputId) => {
    await handleToggleInput(inputId);
  };

  // Get input name by ID
  const getInputName = (inputId) => {
    const input = availableInputs.find((inp) => inp.id === inputId);
    return input ? input.meta_name : inputId;
  };

  const handleSave = async () => {
    if (!selectedLine) return;

    setSaving(true);
    try {
      const formData = {
        business_context: businessContextRef.current,
        calculation_logic: calculationLogicRef.current,
        observations: observationsRef.current,
      };

      await client.put(
        `/api/ctsguide/documentations/${selectedLine.id}/`,
        formData,
      );

      // Update initial values after save
      initialValuesRef.current = { ...formData };
      setHasUnsavedChanges(false);

      await loadDocumentation(selectedLine.id);
      await loadPLLines(true); // Refresh status icons, preserve selection
    } catch (error) {
      console.error("Error saving documentation:", error);
    } finally {
      setSaving(false);
    }
  };

  const handleValidateWithAI = async () => {
    if (!selectedLine) return;

    setValidating(true);
    try {
      const formData = {
        business_context: businessContextRef.current,
        calculation_logic: calculationLogicRef.current,
        observations: observationsRef.current,
      };

      const res = await client.post(
        `/api/ctsguide/documentations/${selectedLine.id}/validate/`,
        formData,
      );

      // Update documentation state without reloading the page
      setDocumentation((prev) => ({
        ...prev,
        ...res.data,
      }));

      const aiRewritten = res.data.ai_rewritten_documentation || "";
      setAiContent(aiRewritten);
      setInitialAiContent(aiRewritten);
      aiContentRef.current = aiRewritten;

      // Update initial values after validation (data was saved)
      initialValuesRef.current = { ...formData };
      setHasUnsavedChanges(false);

      // Note: Status icons will be updated when user approves
    } catch (error) {
      console.error("Error validating documentation:", error);
    } finally {
      setValidating(false);
    }
  };

  const handleApprove = async () => {
    if (!selectedLine) return;

    setApproving(true);
    try {
      // First, save any edits to the AI-rewritten documentation
      if (aiContentRef.current) {
        await client.put(`/api/ctsguide/documentations/${selectedLine.id}/`, {
          ai_rewritten_documentation: aiContentRef.current,
        });
      }
      // Then approve
      await client.post(
        `/api/ctsguide/documentations/${selectedLine.id}/approve/`,
      );
      await loadDocumentation(selectedLine.id);
      await loadPLLines(true); // Refresh status icons, preserve selection
    } catch (error) {
      console.error("Error approving documentation:", error);
    } finally {
      setApproving(false);
    }
  };

  const handleUnlock = async () => {
    if (!selectedLine) return;

    try {
      await client.post(
        `/api/ctsguide/documentations/${selectedLine.id}/unlock/`,
      );
      await loadDocumentation(selectedLine.id);
      await loadPLLines(true); // Refresh status icons, preserve selection
    } catch (error) {
      console.error("Error unlocking documentation:", error);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const response = await client.post(
        "/api/ctsguide/documentation-export/",
        {
          format: exportFormat,
          include_inputs: exportIncludeInputs,
          include_calculations: exportIncludeCalculations,
          only_approved: exportOnlyApproved,
        },
        {
          responseType: "blob",
        },
      );

      // Create download link with correct MIME type and extension
      const mimeTypes = {
        pdf: "application/pdf",
        pptx: "application/vnd.openxmlformats-officedocument.presentationml.presentation",
        xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      };
      const extensions = {
        pdf: "pdf",
        pptx: "pptx",
        xlsx: "xlsx",
      };
      const blob = new Blob([response.data], {
        type: mimeTypes[exportFormat] || mimeTypes.pdf,
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `CTS_Documentation.${extensions[exportFormat] || "pdf"}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      setExportDialogOpen(false);
    } catch (error) {
      console.error("Error exporting documentation:", error);
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className={classes.pageContainer}>
      <Box display="flex" justifyContent="center" mb={0.5}>
        <CTSMiniNav />
      </Box>
      <GridContainer>
        <GridItem xs={12}>
          <Card>
            <CardHeader color="client" icon>
              <CardIcon color="client">
                <Description />
              </CardIcon>
              <h4 className={classes.cardIconTitle}>
                {t("ctsguide.nav.documentation")}
              </h4>
              <div className={classes.headerTabsRight}>
                <Tooltip title={t("ctsguide.documentation.export")}>
                  <IconButton
                    size="small"
                    className={classes.exportButton}
                    onClick={() => setExportDialogOpen(true)}
                  >
                    <GetApp style={{ fontSize: "1.2em" }} />
                  </IconButton>
                </Tooltip>
              </div>
            </CardHeader>
            <CardBody>
              {loading ? (
                <div className={classes.loadingContainer}>
                  <ThreeDots
                    width="120px"
                    color={clientColor}
                    ariaLabel="loading"
                  />
                </div>
              ) : (
                <>
                  <Box mb={2}>
                    <p style={{ color: "#666", fontSize: "14px", margin: 0 }}>
                      {t("ctsguide.documentation.description")}
                    </p>
                  </Box>

                  <div className={classes.mainContainer}>
                    {/* Left sidebar - P&L Tree */}
                    <div className={classes.sidebarContainer}>
                      <div className={classes.plHeader}>
                        <span className={classes.headerTitle}>
                          {t("ctsguide.pl.structure_title")}
                        </span>
                      </div>
                      <div className={classes.plOuterWrapper}>
                        {plLines.length > 0 ? (
                          plLines.map((line) => (
                            <PLLine
                              key={line.id}
                              line={line}
                              selectedLine={selectedLine}
                              onSelect={setSelectedLine}
                              drillDownsOpen={drillDownsOpen}
                              onDrillDownClick={handleDrillDownClick}
                              classes={classes}
                            />
                          ))
                        ) : (
                          <div className={classes.emptyState}>
                            <p>{t("ctsguide.pl.no_lines")}</p>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Main content - Documentation form */}
                    <div className={classes.contentContainer}>
                      {selectedLine && documentation ? (
                        <>
                          {/* Header with collapse toggle */}
                          <div className={classes.contentHeader}>
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                              }}
                            >
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                }}
                              >
                                <h3 className={classes.lineTitle}>
                                  {selectedLine.name}
                                </h3>
                                {hasUnsavedChanges && (
                                  <span className={classes.unsavedIndicator}>
                                    {t(
                                      "ctsguide.documentation.unsaved_changes",
                                    )}
                                  </span>
                                )}
                              </div>
                              <Tooltip
                                title={
                                  rawInputsExpanded
                                    ? t(
                                        "ctsguide.documentation.collapse_inputs",
                                      )
                                    : t("ctsguide.documentation.expand_inputs")
                                }
                              >
                                <IconButton
                                  size="small"
                                  onClick={() =>
                                    setRawInputsExpanded(!rawInputsExpanded)
                                  }
                                  style={{ color: grayColor[3] }}
                                >
                                  {rawInputsExpanded ? (
                                    <ExpandLess />
                                  ) : (
                                    <ExpandMore />
                                  )}
                                </IconButton>
                              </Tooltip>
                            </div>
                          </div>

                          {/* Approved Banner */}
                          {documentation.is_locked && (
                            <div className={classes.lockedBanner}>
                              <div className={classes.lockedBannerText}>
                                <CheckCircle />
                                <span>
                                  {t("ctsguide.documentation.locked_message", {
                                    user: documentation.approved_by_email,
                                    date: new Date(
                                      documentation.approved_at,
                                    ).toLocaleDateString(),
                                  })}
                                </span>
                              </div>
                              <Tooltip
                                title={t("ctsguide.documentation.unlock")}
                              >
                                <span>
                                  <Button
                                    size="sm"
                                    justIcon
                                    color="warning"
                                    onClick={handleUnlock}
                                  >
                                    <LockOpen />
                                  </Button>
                                </span>
                              </Tooltip>
                            </div>
                          )}

                          {/* Form Fields - Collapsible */}
                          {rawInputsExpanded && (
                            <form>
                              {/* Input Selector - Links inputs to this P&L line */}
                              <div className={classes.inputSelectorContainer}>
                                <div className={classes.inputSelectorLabel}>
                                  <Description
                                    style={{
                                      fontSize: 16,
                                      color: grayColor[3],
                                    }}
                                  />
                                  {t("ctsguide.documentation.related_inputs")}
                                  <Tooltip
                                    title={t(
                                      "ctsguide.documentation.related_inputs_hint",
                                    )}
                                  >
                                    <IconButton
                                      size="small"
                                      style={{ padding: 2 }}
                                    >
                                      <Info style={{ fontSize: 14 }} />
                                    </IconButton>
                                  </Tooltip>
                                </div>
                                <div className={classes.inputChipsContainer}>
                                  {selectedInputIds.map((inputId) => (
                                    <Chip
                                      key={inputId}
                                      label={getInputName(inputId)}
                                      size="small"
                                      className={classes.inputChip}
                                      onDelete={
                                        documentation.is_locked
                                          ? undefined
                                          : () => handleRemoveInput(inputId)
                                      }
                                    />
                                  ))}
                                  {!documentation.is_locked && (
                                    <ClickAwayListener
                                      onClickAway={() =>
                                        setInputSelectorOpen(false)
                                      }
                                    >
                                      <div style={{ position: "relative" }}>
                                        <button
                                          type="button"
                                          className={classes.addInputButton}
                                          onClick={() =>
                                            setInputSelectorOpen(
                                              !inputSelectorOpen,
                                            )
                                          }
                                        >
                                          <Add style={{ fontSize: 14 }} />
                                          {selectedInputIds.length === 0 &&
                                            t(
                                              "ctsguide.documentation.add_input",
                                            )}
                                        </button>
                                        {inputSelectorOpen && (
                                          <div
                                            className={classes.inputDropdown}
                                          >
                                            {availableInputs.length === 0 ? (
                                              <div
                                                className={
                                                  classes.inputDropdownEmpty
                                                }
                                              >
                                                {t(
                                                  "ctsguide.documentation.no_inputs_available",
                                                )}
                                              </div>
                                            ) : (
                                              availableInputs.map((input) => (
                                                <div
                                                  key={input.id}
                                                  className={cx(
                                                    classes.inputDropdownItem,
                                                    {
                                                      [classes.inputDropdownItemSelected]:
                                                        selectedInputIds.includes(
                                                          input.id,
                                                        ),
                                                    },
                                                  )}
                                                  onClick={() =>
                                                    handleToggleInput(input.id)
                                                  }
                                                >
                                                  <Checkbox
                                                    size="small"
                                                    checked={selectedInputIds.includes(
                                                      input.id,
                                                    )}
                                                    style={{ padding: 0 }}
                                                  />
                                                  <span>{input.meta_name}</span>
                                                </div>
                                              ))
                                            )}
                                          </div>
                                        )}
                                      </div>
                                    </ClickAwayListener>
                                  )}
                                </div>
                              </div>

                              <div className={classes.formField}>
                                <div className={classes.formLabel}>
                                  {t("ctsguide.documentation.business_context")}
                                  <Tooltip
                                    title={t(
                                      "ctsguide.documentation.business_context_hint_detailed",
                                    )}
                                  >
                                    <IconButton size="small">
                                      <Info fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                  {!documentation.is_locked && (
                                    <AudioRecorder
                                      onTranscription={
                                        handleBusinessContextTranscription
                                      }
                                      apiEndpoint={speechToTextEndpoint}
                                      disabled={saving || validating}
                                    />
                                  )}
                                  <Tooltip
                                    title={t("ctsguide.documentation.expand")}
                                  >
                                    <IconButton
                                      size="small"
                                      className={classes.expandButton}
                                      onClick={() =>
                                        setExpandedEditor("business_context")
                                      }
                                    >
                                      <Fullscreen fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                  <span
                                    className={classes.exampleToggle}
                                    onClick={() =>
                                      setShowBusinessContextExample(
                                        !showBusinessContextExample,
                                      )
                                    }
                                  >
                                    {showBusinessContextExample ? (
                                      <VisibilityOff fontSize="small" />
                                    ) : (
                                      <Visibility fontSize="small" />
                                    )}
                                    <span className={classes.exampleToggleText}>
                                      {showBusinessContextExample
                                        ? t(
                                            "ctsguide.documentation.hide_example",
                                          )
                                        : t(
                                            "ctsguide.documentation.show_example",
                                          )}
                                    </span>
                                  </span>
                                </div>
                                {showBusinessContextExample && (
                                  <div className={classes.exampleContainer}>
                                    <div className={classes.exampleLabel}>
                                      {t(
                                        "ctsguide.documentation.example_label",
                                      )}
                                    </div>
                                    <div
                                      className={classes.exampleInputsNote}
                                      dangerouslySetInnerHTML={{
                                        __html: t(
                                          "ctsguide.documentation.example_inputs_note",
                                        ),
                                      }}
                                    />
                                    <TextEditor
                                      initialContent={t(
                                        "ctsguide.documentation.business_context_example",
                                      )}
                                      readOnly={true}
                                      setContent={() => {}}
                                    />
                                  </div>
                                )}
                                <TextEditor
                                  key={`business_context_${selectedLine.id}_${editorKey}`}
                                  initialContent={initialBusinessContext}
                                  readOnly={documentation.is_locked}
                                  setContent={handleBusinessContextChange}
                                  mentionSuggestions={selectedInputsForMention}
                                />
                              </div>

                              <div className={classes.formField}>
                                <div className={classes.formLabel}>
                                  {t(
                                    "ctsguide.documentation.calculation_logic",
                                  )}
                                  <Tooltip
                                    title={t(
                                      "ctsguide.documentation.calculation_logic_hint_detailed",
                                    )}
                                  >
                                    <IconButton size="small">
                                      <Info fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                  {!documentation.is_locked && (
                                    <AudioRecorder
                                      onTranscription={
                                        handleCalculationLogicTranscription
                                      }
                                      apiEndpoint={speechToTextEndpoint}
                                      disabled={saving || validating}
                                    />
                                  )}
                                  <Tooltip
                                    title={t("ctsguide.documentation.expand")}
                                  >
                                    <IconButton
                                      size="small"
                                      className={classes.expandButton}
                                      onClick={() =>
                                        setExpandedEditor("calculation_logic")
                                      }
                                    >
                                      <Fullscreen fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                  <span
                                    className={classes.exampleToggle}
                                    onClick={() =>
                                      setShowCalculationLogicExample(
                                        !showCalculationLogicExample,
                                      )
                                    }
                                  >
                                    {showCalculationLogicExample ? (
                                      <VisibilityOff fontSize="small" />
                                    ) : (
                                      <Visibility fontSize="small" />
                                    )}
                                    <span className={classes.exampleToggleText}>
                                      {showCalculationLogicExample
                                        ? t(
                                            "ctsguide.documentation.hide_example",
                                          )
                                        : t(
                                            "ctsguide.documentation.show_example",
                                          )}
                                    </span>
                                  </span>
                                </div>
                                {showCalculationLogicExample && (
                                  <div className={classes.exampleContainer}>
                                    <div className={classes.exampleLabel}>
                                      {t(
                                        "ctsguide.documentation.example_label",
                                      )}
                                    </div>
                                    <div
                                      className={classes.exampleInputsNote}
                                      dangerouslySetInnerHTML={{
                                        __html: t(
                                          "ctsguide.documentation.example_inputs_note",
                                        ),
                                      }}
                                    />
                                    <TextEditor
                                      initialContent={t(
                                        "ctsguide.documentation.calculation_logic_example",
                                      )}
                                      readOnly={true}
                                      setContent={() => {}}
                                    />
                                  </div>
                                )}
                                <TextEditor
                                  key={`calculation_logic_${selectedLine.id}_${editorKey}`}
                                  initialContent={initialCalculationLogic}
                                  readOnly={documentation.is_locked}
                                  setContent={handleCalculationLogicChange}
                                  mentionSuggestions={selectedInputsForMention}
                                />
                              </div>

                              <div className={classes.formField}>
                                <div className={classes.formLabel}>
                                  {t("ctsguide.documentation.observations")}
                                  <Tooltip
                                    title={t(
                                      "ctsguide.documentation.observations_hint_detailed",
                                    )}
                                  >
                                    <IconButton size="small">
                                      <Info fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                  {!documentation.is_locked && (
                                    <AudioRecorder
                                      onTranscription={
                                        handleObservationsTranscription
                                      }
                                      apiEndpoint={speechToTextEndpoint}
                                      disabled={saving || validating}
                                    />
                                  )}
                                  <Tooltip
                                    title={t("ctsguide.documentation.expand")}
                                  >
                                    <IconButton
                                      size="small"
                                      className={classes.expandButton}
                                      onClick={() =>
                                        setExpandedEditor("observations")
                                      }
                                    >
                                      <Fullscreen fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                  <span
                                    className={classes.exampleToggle}
                                    onClick={() =>
                                      setShowObservationsExample(
                                        !showObservationsExample,
                                      )
                                    }
                                  >
                                    {showObservationsExample ? (
                                      <VisibilityOff fontSize="small" />
                                    ) : (
                                      <Visibility fontSize="small" />
                                    )}
                                    <span className={classes.exampleToggleText}>
                                      {showObservationsExample
                                        ? t(
                                            "ctsguide.documentation.hide_example",
                                          )
                                        : t(
                                            "ctsguide.documentation.show_example",
                                          )}
                                    </span>
                                  </span>
                                </div>
                                {showObservationsExample && (
                                  <div className={classes.exampleContainer}>
                                    <div className={classes.exampleLabel}>
                                      {t(
                                        "ctsguide.documentation.example_label",
                                      )}
                                    </div>
                                    <TextEditor
                                      initialContent={t(
                                        "ctsguide.documentation.observations_example",
                                      )}
                                      readOnly={true}
                                      setContent={() => {}}
                                    />
                                  </div>
                                )}
                                <TextEditor
                                  key={`observations_${selectedLine.id}_${editorKey}`}
                                  initialContent={initialObservations}
                                  readOnly={documentation.is_locked}
                                  setContent={handleObservationsChange}
                                  mentionSuggestions={selectedInputsForMention}
                                />
                              </div>

                              {/* Action Buttons */}
                              {!documentation.is_locked && (
                                <div className={classes.actionsRow}>
                                  <Tooltip title={t("common.save_draft")}>
                                    <span>
                                      <Button
                                        size="sm"
                                        justIcon
                                        color="system_gray"
                                        onClick={handleSave}
                                        disabled={saving}
                                      >
                                        <Save />
                                      </Button>
                                    </span>
                                  </Tooltip>
                                  {validating ? (
                                    <AiLoadingIndicator
                                      isLoading={validating}
                                      context="documentation"
                                      interval={2000}
                                    />
                                  ) : (
                                    <Tooltip
                                      title={
                                        hasUnsavedChanges
                                          ? t(
                                              "ctsguide.documentation.save_before_validate",
                                            )
                                          : t(
                                              "ctsguide.documentation.validate_with_ai",
                                            )
                                      }
                                    >
                                      <span>
                                        <Button
                                          size="sm"
                                          justIcon
                                          color="info"
                                          onClick={handleValidateWithAI}
                                          disabled={hasUnsavedChanges}
                                        >
                                          <Stars />
                                        </Button>
                                      </span>
                                    </Tooltip>
                                  )}
                                </div>
                              )}
                            </form>
                          )}

                          {/* AI Results */}
                          {aiContent && (
                            <div className={classes.aiResultContainer}>
                              {/* Feedback first - so user reads suggestions before the rewritten doc */}
                              {documentation.ai_feedback &&
                                documentation.ai_feedback.length > 0 && (
                                  <div
                                    style={{
                                      marginBottom: "20px",
                                      padding: "16px",
                                      backgroundColor: "#fff8e1",
                                      borderRadius: "6px",
                                      border: "1px solid #ffe082",
                                    }}
                                  >
                                    <div
                                      className={classes.aiResultTitle}
                                      style={{ marginBottom: "12px" }}
                                    >
                                      <Info style={{ color: "#ff9800" }} />
                                      {t("ctsguide.documentation.ai_feedback")}
                                    </div>
                                    <ul className={classes.feedbackList}>
                                      {documentation.ai_feedback.map(
                                        (feedback, idx) => (
                                          <li
                                            key={idx}
                                            className={classes.feedbackItem}
                                          >
                                            {feedback}
                                          </li>
                                        ),
                                      )}
                                    </ul>
                                  </div>
                                )}

                              {/* AI-Enhanced documentation */}
                              <div className={classes.formField}>
                                <div className={classes.formLabel}>
                                  <CheckCircle
                                    style={{ color: "#4caf50", fontSize: 18 }}
                                  />
                                  {t("ctsguide.documentation.ai_rewritten")}
                                  <Tooltip
                                    title={t(
                                      "ctsguide.documentation.ai_rewritten_hint",
                                    )}
                                  >
                                    <IconButton size="small">
                                      <Info fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </div>
                                <TextEditor
                                  key={`ai_content_${selectedLine.id}_${editorKey}_${documentation.last_ai_validation || ""}`}
                                  initialContent={initialAiContent}
                                  readOnly={documentation.is_locked}
                                  setContent={handleAiContentChange}
                                  mentionSuggestions={selectedInputsForMention}
                                />
                              </div>

                              {!documentation.is_locked && (
                                <Box
                                  mt={3}
                                  display="flex"
                                  justifyContent="flex-end"
                                >
                                  {approving ? (
                                    <ThreeDots
                                      height="30"
                                      width="40"
                                      radius="4"
                                      color="#4caf50"
                                      ariaLabel="approving"
                                      visible={true}
                                    />
                                  ) : (
                                    <Tooltip
                                      title={t(
                                        "ctsguide.documentation.submit_and_lock",
                                      )}
                                    >
                                      <span>
                                        <Button
                                          size="sm"
                                          justIcon
                                          color="success"
                                          onClick={handleApprove}
                                        >
                                          <CheckCircle />
                                        </Button>
                                      </span>
                                    </Tooltip>
                                  )}
                                </Box>
                              )}
                            </div>
                          )}
                        </>
                      ) : (
                        <div className={classes.emptyState}>
                          <Description
                            style={{ fontSize: 48, marginBottom: 16 }}
                          />
                          <p>{t("ctsguide.documentation.select_line")}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </>
              )}
            </CardBody>
          </Card>
        </GridItem>
      </GridContainer>

      {/* Export Dialog */}
      <Dialog
        open={exportDialogOpen}
        onClose={() => setExportDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <ModalHeader
          title={t("ctsguide.documentation.export_title")}
          onClose={() => setExportDialogOpen(false)}
          closeDisabled={exporting}
          closeLabel={t("common.close")}
          clientColor={clientColor}
        />
        <DialogContent className={classes.exportDialogContent}>
          <p style={{ color: "#666", fontSize: "0.9em", marginBottom: "20px" }}>
            {t("ctsguide.documentation.export_description")}
          </p>

          {/* Format Selection */}
          <div className={classes.exportFormatContainer}>
            <span className={classes.exportFormatLabel}>
              {t("ctsguide.documentation.export_format")}
            </span>
            <div className={classes.exportFormatOptions}>
              <div
                className={`${classes.exportFormatOption} ${exportFormat === "pdf" ? classes.exportFormatOptionSelected : ""}`}
                onClick={() => setExportFormat("pdf")}
              >
                <PictureAsPdf
                  className={`${classes.exportFormatIcon} ${exportFormat === "pdf" ? classes.exportFormatIconSelected : ""}`}
                />
                <span className={classes.exportFormatName}>
                  {t("ctsguide.documentation.export_pdf")}
                </span>
              </div>
              <div
                className={`${classes.exportFormatOption} ${exportFormat === "pptx" ? classes.exportFormatOptionSelected : ""}`}
                onClick={() => setExportFormat("pptx")}
              >
                <Slideshow
                  className={`${classes.exportFormatIcon} ${exportFormat === "pptx" ? classes.exportFormatIconSelected : ""}`}
                />
                <span className={classes.exportFormatName}>
                  {t("ctsguide.documentation.export_ppt")}
                </span>
              </div>
              <div
                className={`${classes.exportFormatOption} ${exportFormat === "xlsx" ? classes.exportFormatOptionSelected : ""}`}
                onClick={() => setExportFormat("xlsx")}
              >
                <GetApp
                  className={`${classes.exportFormatIcon} ${exportFormat === "xlsx" ? classes.exportFormatIconSelected : ""}`}
                />
                <span className={classes.exportFormatName}>
                  {t("ctsguide.documentation.export_excel")}
                </span>
              </div>
            </div>
          </div>

          {/* Options */}
          <div className={classes.exportOptionsContainer}>
            <FormControlLabel
              className={classes.exportOptionItem}
              control={
                <Checkbox
                  checked={exportIncludeInputs}
                  onChange={(e) => setExportIncludeInputs(e.target.checked)}
                  color="primary"
                  size="small"
                />
              }
              label={t("ctsguide.documentation.export_include_inputs")}
            />
            <FormControlLabel
              className={classes.exportOptionItem}
              control={
                <Checkbox
                  checked={exportIncludeCalculations}
                  onChange={(e) =>
                    setExportIncludeCalculations(e.target.checked)
                  }
                  color="primary"
                  size="small"
                />
              }
              label={t("ctsguide.documentation.export_include_calculations")}
            />
            <FormControlLabel
              className={classes.exportOptionItem}
              control={
                <Checkbox
                  checked={exportOnlyApproved}
                  onChange={(e) => setExportOnlyApproved(e.target.checked)}
                  color="primary"
                  size="small"
                />
              }
              label={t("ctsguide.documentation.export_only_approved")}
            />
          </div>
        </DialogContent>
        <DialogActions style={{ padding: "16px 24px" }}>
          <Button
            size="sm"
            color="system_gray"
            onClick={() => setExportDialogOpen(false)}
            disabled={exporting}
          >
            {t("common.cancel")}
          </Button>
          <Button
            size="sm"
            color="client"
            onClick={handleExport}
            disabled={exporting}
          >
            {exporting ? (
              <ThreeDots height="16" width="40" color={clientTextColor} />
            ) : (
              <>
                <GetApp style={{ marginRight: "4px" }} />
                {t("ctsguide.documentation.export")}
              </>
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Expanded Editor Dialog */}
      <Dialog
        open={expandedEditor !== null}
        onClose={() => setExpandedEditor(null)}
        className={classes.expandedDialog}
        maxWidth={false}
      >
        <ModalHeader
          title={
            <>
              {expandedEditor === "business_context" &&
                t("ctsguide.documentation.business_context")}
              {expandedEditor === "calculation_logic" &&
                t("ctsguide.documentation.calculation_logic")}
              {expandedEditor === "observations" &&
                t("ctsguide.documentation.observations")}
            </>
          }
          subtitleParts={[
            t("ctsguide.documentation.title"),
            selectedLine?.name ? (
              <strong key="line">{selectedLine.name}</strong>
            ) : null,
          ].filter(Boolean)}
          rightContent={
            !documentation?.is_locked && expandedEditor ? (
              <AudioRecorder
                onTranscription={(text) => {
                  if (expandedEditor === "business_context") {
                    handleBusinessContextTranscription(text);
                  } else if (expandedEditor === "calculation_logic") {
                    handleCalculationLogicTranscription(text);
                  } else if (expandedEditor === "observations") {
                    handleObservationsTranscription(text);
                  }
                }}
                apiEndpoint={speechToTextEndpoint}
                disabled={saving || validating}
              />
            ) : null
          }
          onClose={() => setExpandedEditor(null)}
          closeLabel={t("common.close")}
          clientColor={clientColor}
        />
        <DialogContent className={classes.expandedDialogContent}>
          {expandedEditor === "business_context" && (
            <TextEditor
              key={`expanded_business_context_${selectedLine?.id}_${editorKey}`}
              initialContent={initialBusinessContext}
              readOnly={documentation?.is_locked}
              setContent={handleBusinessContextChange}
              mentionSuggestions={selectedInputsForMention}
            />
          )}
          {expandedEditor === "calculation_logic" && (
            <TextEditor
              key={`expanded_calculation_logic_${selectedLine?.id}_${editorKey}`}
              initialContent={initialCalculationLogic}
              readOnly={documentation?.is_locked}
              setContent={handleCalculationLogicChange}
              mentionSuggestions={selectedInputsForMention}
            />
          )}
          {expandedEditor === "observations" && (
            <TextEditor
              key={`expanded_observations_${selectedLine?.id}_${editorKey}`}
              initialContent={initialObservations}
              readOnly={documentation?.is_locked}
              setContent={handleObservationsChange}
              mentionSuggestions={selectedInputsForMention}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Floating Feedback Button */}
      <FeedbackButton pageOrigin="documentation" />
    </div>
  );
}
